import type { ReactNode } from "react";
import { classNames } from "./internal/class-names";
import {
  OperationalIcon,
  type OperationalIconName,
} from "./operational-icon";

export type StatusTone = "danger" | "info" | "neutral" | "success" | "warning";

/**
 * Each tone carries a distinct SHAPE as well as a distinct colour, and the
 * label text is always visible. Meaning is therefore never conveyed by colour
 * alone (WCAG 1.4.1) and the pill still reads correctly in `high-contrast`.
 */
const toneIcon: Record<StatusTone, OperationalIconName> = {
  danger: "close",
  info: "info",
  neutral: "circle",
  success: "check",
  warning: "warning",
};

export interface StatusPillProps {
  /** Visible status text. Required — a pill is never a bare coloured dot. */
  readonly label: ReactNode;
  readonly tone?: StatusTone | undefined;
  /**
   * A more specific semantic glyph when the tone alone is ambiguous, such as
   * danger-coloured operational priority versus a rejected/failed status.
   */
  readonly icon?: OperationalIconName | undefined;
  /** Prefix read by assistive technology, e.g. "Readiness". */
  readonly srPrefix?: string | undefined;
  readonly className?: string | undefined;
}

export function StatusPill({
  className,
  icon,
  label,
  srPrefix,
  tone = "neutral",
}: StatusPillProps): ReactNode {
  return (
    <span className={classNames("thw-status-pill", `thw-status-pill--${tone}`, className)}>
      <span aria-hidden="true" className="thw-status-pill__glyph">
        <OperationalIcon name={icon ?? toneIcon[tone]} />
      </span>
      {srPrefix === undefined ? null : <span className="sr-only">{`${srPrefix}: `}</span>}
      <span>{label}</span>
    </span>
  );
}

export const StatusChip = StatusPill;
